import { graphql, PageProps } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'

import { LayoutProvider } from '@application/Contexts/LayoutContext'
import { PageBuilder } from '@application/Interfaces/Features/PageBuilder/PageBuilder'
import type {
    HelpCenterMainType,
    LocalSearchType,
    PageNodeType,
    SingleTypeComponentType,
} from '@application/Types/ComponentTypes'
import type { Locale } from '@application/Types/Locale'
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react'
import { isGdprCookieAccepted } from '@infrastructure/Cookies'
import { GrowthBookFeaturesType } from '@infrastructure/Growthbook'
import { growthbookTrackingCallback } from '@infrastructure/Growthbook/utils'
import { localizeUrl } from '@infrastructure/Url/localizeUrls/localizeUtilities'
import { StrapiTranslations, Translations } from '@src/Application/Types/TranslationTypes'
import { STRAPI_COMPONENT_TYPES } from '@src/Core/Constants/PageConstants'
import { GlobalAppStyles } from '@src/GlobalAppStyles'
import { Context } from '@src/Infrastructure/Context'
import { OrganizationProvider } from '@src/Infrastructure/Context/OrganizationContext'
import { getLocalSearchLocalWise, getTranslations, modifiedHeroComponents } from '@src/templates/utils'

const growthbook = new GrowthBook<GrowthBookFeaturesType>({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: `${process.env.GATSBY_GROWTHBOOK_CLIENT_KEY}`,
    subscribeToChanges: true,
    // Only required for A/B testing
    // Called every time a user is put into an experiment
    trackingCallback: growthbookTrackingCallback,
})

const Template = (
    props: PageProps<
        {
            strapiPage: PageNodeType
            localSearchHelpCenter: LocalSearchType
            allStrapiHelpCenterCategory: { group: { nodes: HelpCenterMainType['categories'] }[] }
            strapiTranslation: StrapiTranslations
        },
        { language: Locale; singleTypeComponents: SingleTypeComponentType }
    >
): JSX.Element => {
    const [sendMixpanelEvents, setSendMixpanelEvents] = useState(isGdprCookieAccepted('mixpanel'))
    const locale = props.pageContext.language

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        },
    })

    const strapiTranslation = props.data.strapiTranslation

    const translations: Translations = getTranslations(strapiTranslation)

    const component = props.data.strapiPage.pageComponents?.find(
        (p) => p.strapi_component === STRAPI_COMPONENT_TYPES.HELP_CENTER_LIST
    ) as HelpCenterMainType

    if (component) {
        component.categories = props.data.allStrapiHelpCenterCategory?.group.map((g) => {
            const category = g.nodes[0]
            return {
                ...category,
                help_centers: category.help_centers.map((article) => {
                    return {
                        ...article,
                        pageUrl: localizeUrl(article.language, article.pageUrl),
                    }
                }),
                categoryUrl: localizeUrl(category.language, category.categoryUrl),
            }
        })
    }

    const pageComponents = props.data.strapiPage.pageComponents ?? []

    const singleTypeUSP = props.pageContext.singleTypeComponents['usp'][locale]
    const singleTypeAgilitySection = props.pageContext.singleTypeComponents['agilitySection'][locale]
    const helpCenterLocalSearch = getLocalSearchLocalWise({
        localSearchHelpCenter: props.data.localSearchHelpCenter,
        locale,
    })

    const modifiedPageComponents = modifiedHeroComponents({
        pageComponents,
        singleTypeUSP,
        singleTypeAgilitySection,
        helpCenterLocalSearch,
    })

    useEffect(() => {
        // Load features asynchronously when the app renders
        growthbook.loadFeatures()
    }, [])

    return (
        <Context.Provider
            value={{
                locale,
                translations,
                sendMixpanelEvents,
                setSendMixpanelEvents,
            }}
        >
            <OrganizationProvider>
                <QueryClientProvider client={queryClient}>
                    <LayoutProvider>
                        <GrowthBookProvider growthbook={growthbook}>
                            <GlobalAppStyles />
                            <PageBuilder
                                {...{
                                    ...props,
                                    data: {
                                        ...props.data,
                                        strapiPage: {
                                            ...props.data.strapiPage,
                                            pageComponents: modifiedPageComponents,
                                        },
                                    },
                                }}
                            />
                        </GrowthBookProvider>
                    </LayoutProvider>
                </QueryClientProvider>
            </OrganizationProvider>
        </Context.Provider>
    )
}

export default Template

export const query = graphql`
    query ($id: String!, $language: String!) {
        strapiPage(id: { eq: $id }) {
            ...StrapiPageData
        }
        localSearchHelpCenter {
            index
            store
        }
        allStrapiHelpCenterCategory(
            filter: { language: { eq: $language }, categoryUrl: { regex: "/^((?!do-not-remove).)*$/" } }
        ) {
            group(field: { id: SELECT }) {
                nodes {
                    help_centers {
                        title {
                            data {
                                title
                            }
                        }
                        pageUrl
                        legacyPageUrl
                        useLegacyPageUrl
                        language
                    }
                    categoryUrl
                    language
                    title
                    description {
                        data {
                            description
                            childMarkdownRemark {
                                html
                            }
                        }
                    }
                    image {
                        alternativeText
                        localFile {
                            childImageSharp {
                                gatsbyImageData
                            }
                            publicURL
                            extension
                        }
                    }
                }
            }
        }
        strapiTranslation {
            ...StrapiTranslations
        }
    }
`
